<template>
  <card>
    <div slot="header" class="clinic-prices__header">
      <span class="clinic-prices__title">Operation prices</span>
      <span class="clinic-prices__count" v-if="rows.length">
        {{ priced }} / {{ rows.length }} priced
      </span>
    </div>

    <div class="clinic-prices__toolbar">
      <label class="clinic-prices__toolbar-field">
        <span>Currency</span>
        <el-select
          v-model="currency"
          filterable
          placeholder="Currency"
          value-key="code"
          class="clinic-prices__currency-select"
        >
          <el-option
            v-for="c in currencies"
            :key="c.code"
            :label="c.code"
            :value="c.code"
          />
        </el-select>
      </label>

      <label class="clinic-prices__toolbar-field clinic-prices__toolbar-search">
        <span>Search</span>
        <el-input
          v-model="search"
          placeholder="Filter operations by name"
          clearable
        />
      </label>

      <div class="clinic-prices__toolbar-spacer"></div>

      <base-button type="primary" :disabled="saving" @click="save">
        {{ saving ? 'Saving...' : 'Save prices' }}
      </base-button>
    </div>

    <div v-if="!clinic.operation_ids || !clinic.operation_ids.length" class="card-body">
      Link operations to this clinic first.
    </div>

    <table class="table table-responsive clinic-prices__table" v-else-if="filteredRows.length">
      <thead>
        <tr>
          <th>Operation</th>
          <th class="clinic-prices__num">Price from</th>
          <th class="clinic-prices__num">Price to</th>
          <th>Fixed</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in filteredRows"
          :key="row.entity_id"
          :class="{ 'clinic-prices__row--dirty': dirtyRowIds.has(row.entity_id) }"
        >
          <td>{{ row.name }}</td>
          <td class="clinic-prices__num">
            <el-input-number
              v-model="row.price_from"
              :min="0"
              :controls="false"
              @change="onPriceFromChange(row)"
            />
          </td>
          <td class="clinic-prices__num">
            <el-input-number
              v-model="row.price_to"
              :min="0"
              :controls="false"
              :disabled="row.fixed"
            />
          </td>
          <td>
            <el-checkbox v-model="row.fixed" @change="onFixedToggle(row)"/>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else class="card-body">No operations match the search.</div>
  </card>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';

const DEFAULT_CURRENCY = 'USD';

export default {
  data: () => ({
    currency: DEFAULT_CURRENCY,
    originalCurrency: DEFAULT_CURRENCY,
    search: '',
    rows: [],
    saving: false,
    originalSnapshot: {},
  }),
  computed: {
    ...mapState('clinics', ['clinic']),
    ...mapState('cmsApiCatalogs', ['operations']),
    ...mapState('data', ['currencies']),
    ...mapGetters('prices', ['operationPrices']),
    filteredRows() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter((r) => (r.name || '').toLowerCase().includes(q));
    },
    priced() {
      return this.rows.filter((r) => r.price_from > 0).length;
    },
    dirtyRowIds() {
      const currencyChanged = this.currency !== this.originalCurrency;
      const ids = new Set();
      for (const r of this.rows) {
        const snap = this.originalSnapshot[r.entity_id];
        if (!snap) {
          if (r.price_from > 0) ids.add(r.entity_id);
          continue;
        }
        if (currencyChanged && r.price_from > 0) { ids.add(r.entity_id); continue; }
        if (r.price_from !== snap.price_from || r.price_to !== snap.price_to || r.fixed !== snap.fixed) {
          ids.add(r.entity_id);
        }
      }
      return ids;
    },
  },
  watch: {
    'clinic.id': {
      immediate: true,
      handler(id) {
        if (id) this.loadPrices();
      },
    },
    operationPrices() {
      this.rebuildRows();
    },
    'clinic.operation_ids'() {
      this.rebuildRows();
    },
    operations() {
      this.rebuildRows();
    },
  },
  mounted() {
    if (!this.currencies || !this.currencies.length) this.getCurrencies();
  },
  methods: {
    ...mapActions('prices', ['getClinicPrices', 'upsert']),
    ...mapActions('data', ['getCurrencies']),
    async loadPrices() {
      await this.getClinicPrices({
        clinicId: this.clinic.id,
        params: { entity: 'operations' },
      });
      this.rebuildRows();
      const existingCurrency = this.operationPrices[0]?.currency;
      if (existingCurrency) {
        this.currency = existingCurrency;
        this.originalCurrency = existingCurrency;
      } else {
        this.originalCurrency = this.currency;
      }
    },
    rebuildRows() {
      if (!this.clinic || !this.clinic.operation_ids) {
        this.rows = [];
        this.originalSnapshot = {};
        return;
      }
      const priceByOpId = new Map(this.operationPrices.map((p) => [p.record_id, p]));
      const opById = new Map((this.operations || []).map((op) => [op.id, op]));

      this.rows = this.clinic.operation_ids.map((opId) => {
        const price = priceByOpId.get(opId) || {};
        const op = opById.get(opId);
        const priceFrom = Number(price.price_from) || 0;
        const priceTo = Number(price.price_to) || 0;
        return {
          entity_id: opId,
          id: price.id || 0,
          name: op?.name?.en || `Operation #${opId}`,
          price_from: priceFrom,
          price_to: priceTo,
          fixed: priceFrom > 0 && priceFrom === priceTo,
        };
      });

      const snap = {};
      for (const r of this.rows) {
        snap[r.entity_id] = { price_from: r.price_from, price_to: r.price_to, fixed: r.fixed };
      }
      this.originalSnapshot = snap;
    },
    onPriceFromChange(row) {
      if (row.fixed) row.price_to = row.price_from;
    },
    onFixedToggle(row) {
      if (row.fixed) row.price_to = row.price_from;
    },
    async save() {
      if (!this.currency) {
        this.$notify({ type: 'warning', title: 'Select a currency first' });
        return;
      }
      const currencyChanged = this.currency !== this.originalCurrency;
      const payload = this.rows
        .filter((r) => {
          const snap = this.originalSnapshot[r.entity_id];
          if (!snap) return r.price_from > 0;
          if (currencyChanged && r.price_from > 0) return true;
          return (
            r.price_from !== snap.price_from ||
            r.price_to !== snap.price_to ||
            r.fixed !== snap.fixed
          );
        })
        .map((r) => ({
          id: r.id,
          entity: 'operations',
          entity_id: r.entity_id,
          clinic_id: this.clinic.id,
          price_from: r.price_from,
          price_to: r.fixed ? r.price_from : r.price_to,
          currency: this.currency,
        }));

      if (!payload.length) {
        this.$notify({ type: 'warning', title: 'Nothing to save' });
        return;
      }

      this.saving = true;
      try {
        await this.upsert(payload);
        await this.loadPrices();
        this.$notify({ type: 'success', title: 'Prices saved' });
      } catch (e) {
        this.$notify({
          type: 'danger',
          title: e?.response?.data?.error?.code || 'Save failed',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.clinic-prices {
  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  &__title {
    font-size: 18px;
    font-weight: 600;
  }
  &__count {
    font-size: 13px;
    color: #888;
  }
  &__toolbar {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    padding: 0 16px 12px;
    flex-wrap: wrap;
    &-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
      span {
        font-size: 12px;
        color: #666;
      }
    }
    &-search {
      flex: 1;
      min-width: 200px;
    }
    &-spacer {
      flex: 0;
    }
  }
  &__currency-select {
    width: 120px;
  }
  &__table {
    margin: 0 16px;
    th {
      background: #fafafa;
      font-size: 12px;
      text-transform: uppercase;
      color: #666;
    }
  }
  &__num {
    text-align: right;
    width: 140px;
  }
  &__row--dirty {
    background: #f5fbe6;
  }
}
</style>
